import { Modal, SubmitButton } from "@xola/ui-kit";
import { handleSuccessfulSubmission, PurchasePriceStrategy } from "common";
import { pick } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import {
    ApplyCode,
    ExperiencePurchaseItem,
    LineItem,
    Purchase,
    PurchaseItem,
    PurchaseRuleLinkEntityObject,
    PurchaseStatus,
    SelectionState,
} from "types";
import { useDebounce } from "use-debounce";
import { useInvalidate } from "../../hooks/useInvalidation";
import { isPaymentCoupon, isVoucherCoupon } from "../../models/Coupon";
import {
    CreatePurchaseItemLineItem,
    CreatePurchaseItemState,
    CreatePurchaseLineItem,
    useCreatePurchaseStore,
} from "../../stores/createPurchaseStore";
import { NotifyCustomerCheckbox } from "../Common/NotifyCustomerCheckbox";
import { SecondaryButton } from "../Common/SecondaryButton";
import { allowedKeysToMapPurchaseToStore } from "../PurchaseCreate/helpers/PurchaseCreateHelpers";
import { usePurchaseResponseStore } from "../PurchaseCreate/store/usePurchaseResponseStore";
import { PaymentBreakdown, PriceStrategy } from "../PurchaseModification/PaymentBreakdown";
import { ApplyCodeForm } from "./ApplyCodeForm";
import { lineItemsToIgnoreWhenApplyingCode, useApplySubmitCodeForm, ValidationModal } from "./ApplySubmitCodeHelper";
import { queryClient } from "@/api/queryClient";
import { isMistPurchase } from "@/models/CreatePurchaseState";
import { isCodeSupported, isExperiencePurchaseItemType, isPriceConfigurationUpdated } from "@/models/PurchaseItem";
import { shouldRetainPriceByDefault } from "@/models/Seller";

interface ApplyCouponModalProps {
    onClose?: () => void;
    isInline?: boolean;
    purchase?: Purchase;
    item?: ExperiencePurchaseItem | CreatePurchaseItemState;
}

export const ApplySubmitCodeForm = React.memo(({ purchase, item, onClose, isInline }: ApplyCouponModalProps) => {
    const invalidate = useInvalidate();

    const [notifyGuests, setNotifyGuests] = useState<boolean>(true);
    const [selectedCode, setSelectedCode] = useState<ApplyCode | undefined>();
    const [selectedProducts, setSelectedProducts] = useState<SelectionState | undefined>({ selected: [], all: false });
    const [amount, setAmount] = useState<number | undefined>();
    const [debouncedAmount, debouncedAmountCallback] = useDebounce(amount, 300);
    const isPendingDebounceAmount = debouncedAmountCallback.isPending();
    const [showRetainPriceToggle, setShowRetainPriceToggle] = useState(false);
    const [retainPrice, setRetainPrice] = useState(!shouldRetainPriceByDefault());

    const [voucher, setVoucher] = useState("");
    const [inputValue, setInputValue] = useState<string>();
    const isMistCreatePurchase = useCreatePurchaseStore((state) => state.isMist());
    const isMist = purchase ? isMistPurchase(purchase) : isMistCreatePurchase;

    const { applyCouponCode, validationModalState, setValidationModalState } = useApplySubmitCodeForm({
        selectedProducts,
        notifyGuests,
        showRetainPriceToggle,
        retainPrice,
        inputValue,
    });

    const handleSelectCode = useCallback(
        (code?: ApplyCode, newSelectedProducts?: SelectionState) => {
            const getFinalizedProducts = () => {
                const finalPurchase = purchase ?? useCreatePurchaseStore.getState().purchase;
                const totalExperiencesLength = (
                    finalPurchase.items as Array<PurchaseItem | CreatePurchaseItemState>
                ).filter((item) => isCodeSupported(item.object)).length;

                if (isMist && code?.object === PurchaseRuleLinkEntityObject.AFFILIATE) {
                    return { selected: [], all: true };
                }

                if (!isMist || newSelectedProducts || (code?.coupon && isPaymentCoupon(code.coupon))) {
                    if (totalExperiencesLength === newSelectedProducts?.selected?.length) {
                        return { ...newSelectedProducts, all: true };
                    }

                    return newSelectedProducts;
                }

                return selectedProducts && selectedProducts.selected.length > 0 ? selectedProducts : undefined;
            };

            setSelectedProducts(getFinalizedProducts());
            setSelectedCode(code);
        },
        [selectedProducts, isMist, purchase],
    );

    const handleApply = useCallback(
        async (e: any, isVerificationRequest = false) => {
            const purchaseId = purchase?.id ?? useCreatePurchaseStore.getState().purchaseId;

            try {
                useCreatePurchaseStore.getState().actions.setIsFetchingPreviewPurchase(true);
                const purchaseResponse = await applyCouponCode.mutateAsync({
                    code: selectedCode,
                    purchaseId: purchaseId!,
                    amount: debouncedAmount,
                    voucher,
                    isVerificationRequest,
                });

                if (purchaseResponse?.status === PurchaseStatus.DRAFT) {
                    usePurchaseResponseStore.getState().actions.setPurchase(purchaseResponse);
                    useCreatePurchaseStore.getState().actions.setPurchase((draft) => {
                        for (const [index, item] of purchaseResponse.items.entries()) {
                            const lineItems = item.lineItems
                                .filter((lineItem) => !lineItemsToIgnoreWhenApplyingCode.includes(lineItem.type))
                                .map((lineItem) => pick(lineItem, allowedKeysToMapPurchaseToStore));

                            draft.items[index].lineItems = lineItems as CreatePurchaseItemLineItem[];
                        }

                        draft.lineItems = purchaseResponse.lineItems.map((lineItem: LineItem) => {
                            return pick(lineItem, allowedKeysToMapPurchaseToStore) as CreatePurchaseLineItem;
                        });
                    });
                    useCreatePurchaseStore.setState({
                        balance: purchaseResponse.balance,
                        dueNow: purchaseResponse.dueNow,
                        amount: purchaseResponse.amount,
                    });
                }
            } catch {
                // If there is any error in coupon applying, don't close the modal
                return;
            } finally {
                setTimeout(() => {
                    useCreatePurchaseStore.getState().actions.setIsFetchingPreviewPurchase(false);
                }, 800);
            }

            if (onClose) {
                handleSuccessfulSubmission(() => {
                    onClose();
                    invalidate("codes");
                    // Just using invalidate function will fetch latest purchases again, which is not required.
                    queryClient.invalidateQueries(["purchases", purchaseId]);
                });
            } else {
                queryClient.invalidateQueries(["purchases", purchaseId]);
                invalidate("codes");
            }

            // Clear selected code once applied successfully.
            setSelectedCode(undefined);
        },
        [applyCouponCode, debouncedAmount, invalidate, onClose, selectedCode, voucher, purchase?.id],
    );

    // Commenting out the code below untill the related API issue is fixed
    // https://xola01.atlassian.net/browse/X2-7407?focusedCommentId=115284

    // useEffect(() => {
    //     if (isInline && debouncedAmount) {
    //         handleApply(null);
    //     }
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [debouncedAmount, isInline]); // handleApply should not be a dependency! It will cause infinite loop

    const handleInputChange = useCallback((value?: string) => {
        setInputValue(value?.replace(/[\s<>&"']/g, ""));
    }, []);

    const handleApiValidationSubmit = (e: any) => {
        handleApply(e, true);
        setValidationModalState({ isOpen: false, validationLevel: "", message: "" });
    };

    const handleApiValidationClose = () => {
        setValidationModalState({ ...validationModalState, isOpen: false });
    };

    return isInline ? (
        <fieldset disabled={applyCouponCode.isLoading}>
            <ApplyCodeForm
                item={item}
                purchase={purchase}
                isLoading={isPendingDebounceAmount}
                onSelectCode={handleSelectCode}
                onSelectAmount={setAmount}
                onSelectVoucher={setVoucher}
                onInputChange={handleInputChange}
                onApply={handleApply}
            />

            <ValidationModal
                validationModalState={validationModalState}
                onClose={handleApiValidationClose}
                onSubmit={handleApiValidationSubmit}
            />
        </fieldset>
    ) : (
        <fieldset disabled={applyCouponCode.isLoading}>
            <ApplyCouponModalContent
                item={item}
                purchase={purchase}
                notifyGuests={notifyGuests}
                isLoading={applyCouponCode.isLoading || isPendingDebounceAmount}
                isSuccess={applyCouponCode.isSuccess}
                selectedProducts={selectedProducts}
                showRetainPriceToggle={showRetainPriceToggle}
                selectedCode={selectedCode}
                retainPrice={retainPrice}
                setRetainPrice={setRetainPrice}
                amount={debouncedAmount}
                onNotifyGuestsChange={setNotifyGuests}
                onClose={onClose}
                onApply={handleApply}
                onSelectCode={handleSelectCode}
                onSelectAmount={setAmount}
                onSelectVoucher={setVoucher}
                onRetainToggleChange={setShowRetainPriceToggle}
            />
            <ValidationModal
                validationModalState={validationModalState}
                onClose={handleApiValidationClose}
                onSubmit={handleApiValidationSubmit}
            />
        </fieldset>
    );
});

ApplySubmitCodeForm.displayName = "ApplySubmitCodeForm";

interface ApplyCouponModalContentProps {
    purchase?: Purchase;
    item?: ExperiencePurchaseItem | CreatePurchaseItemState;
    amount?: number;
    selectedCode?: ApplyCode;
    notifyGuests: boolean;
    isLoading: boolean;
    isSuccess: boolean;
    showRetainPriceToggle: boolean;
    retainPrice: boolean;
    selectedProducts?: SelectionState;
    onNotifyGuestsChange: (checked: boolean) => void;
    onClose?: () => void;
    onApply: (e: any) => void;
    onSelectCode: (code?: ApplyCode, state?: any) => void;
    onSelectAmount: (amount: number | undefined) => void;
    onSelectVoucher: (voucher: string) => void;
    setRetainPrice: (value: boolean) => void;
    onRetainToggleChange: (value: boolean) => void;
}

export const ApplyCouponModalContent = ({
    item,
    purchase: postPurchase,
    amount,
    retainPrice,
    selectedCode,
    notifyGuests,
    isLoading,
    isSuccess,
    showRetainPriceToggle,
    selectedProducts,
    onNotifyGuestsChange,
    onClose,
    onApply,
    onSelectCode,
    onSelectAmount,
    onSelectVoucher,
    setRetainPrice,
    onRetainToggleChange,
}: ApplyCouponModalContentProps) => {
    const createPurchase = usePurchaseResponseStore((state) => state.purchase as Purchase);
    const purchase = postPurchase ?? createPurchase;
    const isDraft = purchase.status === PurchaseStatus.DRAFT;
    const isMist = isMistPurchase(purchase);

    const isMistNotSelected =
        isMist && (!selectedProducts || (selectedProducts?.selected.length === 0 && !selectedProducts?.all));

    const isDisabledSubmit = !selectedCode || (isMistNotSelected && !isPaymentCoupon(selectedCode.coupon));

    const paymentBreakdownPayload = useMemo(() => {
        if (selectedCode) {
            const affiliateData = { code: selectedCode?.code, affiliateDeposit: amount };

            if (isMistNotSelected && !isPaymentCoupon(selectedCode.coupon)) {
                return { ops: [] };
            }

            const opData = {
                path: "/",
                op: "addCode",
                data: selectedCode.affiliateNotification ? affiliateData : { code: selectedCode?.code, amount: amount },
            };
            let ops: any[] = [opData];

            const isAllProductSelected = selectedProducts?.all && selectedProducts.selected.length === 0;
            if (selectedProducts && !isAllProductSelected) {
                if (isVoucherCoupon(selectedCode.coupon)) {
                    ops = [
                        {
                            ...opData,
                            data: {
                                ...opData.data,
                                appliedOn: selectedProducts.selected.map((selectedItem) => ({
                                    id: selectedItem,
                                })),
                            },
                        },
                    ];
                } else {
                    ops = selectedProducts.selected.map((id) => {
                        return {
                            ...opData,
                            path: `/items/${id}/lineItems`,
                        };
                    });
                }
            }

            return {
                ops: ops,
                _priceStrategy: showRetainPriceToggle && retainPrice ? PriceStrategy.RETAIN : undefined,
            };
        }
    }, [selectedCode, amount, selectedProducts, isMistNotSelected, showRetainPriceToggle, retainPrice]);

    const handleBreakdownUpdate = (purchase: Purchase | null) => {
        if (showRetainPriceToggle) {
            return;
        }

        const { activeItemIndex } = useCreatePurchaseStore.getState();
        const item = usePurchaseResponseStore.getState().purchase.items?.[activeItemIndex] as ExperiencePurchaseItem;

        if (item && isExperiencePurchaseItemType(item) && isPriceConfigurationUpdated(purchase, item)) {
            onRetainToggleChange(true);
            setRetainPrice(!retainPrice);
        }
    };

    return (
        <>
            <Modal.Header>Apply Code</Modal.Header>
            <Modal.Body className="flex space-x-4">
                <div className="flex-1">
                    {showRetainPriceToggle && (
                        <PurchasePriceStrategy
                            isChecked={retainPrice}
                            isVisible={showRetainPriceToggle}
                            onChange={() => setRetainPrice(!retainPrice)}
                        />
                    )}
                    <ApplyCodeForm
                        item={item}
                        purchase={purchase}
                        onSelectCode={onSelectCode}
                        onSelectAmount={onSelectAmount}
                        onSelectVoucher={onSelectVoucher}
                    />
                </div>

                <div className="flex-1 bg-gray-lighter p-5">
                    {purchase && (
                        <PaymentBreakdown
                            isEditable={false}
                            purchase={purchase}
                            payload={paymentBreakdownPayload}
                            onUpdate={handleBreakdownUpdate}
                        />
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer className="flex justify-end">
                {!isDraft && (
                    <NotifyCustomerCheckbox
                        isChecked={notifyGuests}
                        onChange={(e) => onNotifyGuestsChange(e.target.checked)}
                    />
                )}
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                <SubmitButton disabled={isDisabledSubmit} isLoading={isLoading} isSuccess={isSuccess} onClick={onApply}>
                    Apply
                </SubmitButton>
            </Modal.Footer>
        </>
    );
};
